import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {VStack} from 'platform/foundation';
import {z} from 'zod';

import {isEmpty} from 'ramda';

import {useAttachTelephonyLineNumberMutation, useGetTelephonyLinesQuery} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

interface AttachLineToNumberFormProps extends RequiredTestIdProps {
  numberSid: string;
}

const attachLineToNumberSchema = z.object({
  lineId: z.string().min(1),
});

type AttachLineToNumberFormValues = z.infer<typeof attachLineToNumberSchema>;

/**
 * The mirror of AttachTelephonyNumberForm: that one picks a number for a known line, this one
 * picks a line for a number that currently answers nowhere. Same endpoint underneath.
 */
export function AttachLineToNumberForm(props: AttachLineToNumberFormProps) {
  const {data: lines, isLoading, isError} = useGetTelephonyLinesQuery();

  const [attachNumber] = useAttachTelephonyLineNumberMutation();

  const options = (lines?.lines ?? []).map((line) => ({label: line.name, value: line.id}));

  const handleSubmit: FormSubmitHandler<AttachLineToNumberFormValues> = async (values) => {
    await attachNumber({lineId: values.lineId, numberSid: props.numberSid})
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
        closeCurrentDialog();
      })
      .catch(handleApiError);
  };

  return (
    <DataStatus
      isLoading={isLoading}
      isError={isError}
      isEmpty={isEmpty(options)}
      minHeight={20}
      data-testid={suffixTestId('lines', props)}
    >
      <Form<AttachLineToNumberFormValues>
        experimentalZodSchema={attachLineToNumberSchema}
        onSubmit={handleSubmit}
        defaultValues={{lineId: ''}}
      >
        {(control) => (
          <VStack spacing={4}>
            <FormField
              control={control}
              name="lineId"
              type="choice"
              label={i18n.t('page.settings.telephony.lineLabel')}
              options={options}
              isRequired
              menuInPortal
              data-testid={suffixTestId('lineId', props)}
            />
            <ButtonGroup align="right">
              <Button
                variant="secondary"
                title={i18n.t('general.actions.cancel')}
                onClick={closeCurrentDialog}
                data-testid={suffixTestId('cancel', props)}
              />
              <FormButton
                control={control}
                type="submit"
                variant="primary"
                title={i18n.t('page.settings.telephony.connectToLine')}
                data-testid={suffixTestId('submit', props)}
              />
            </ButtonGroup>
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}
